import { ArrowRight } from 'lucide-react';
import { useState, type FC, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';

import { copyrightStyle } from '@/features/storefront/appearance';
import { subscribeToNewsletter } from '@/features/storefront/api/storefront';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import { LanguageSwitcherSlot } from '@/features/storefront/i18n/LanguageSwitcher';
import { AppStoreBadges } from '@/features/storefront/layouts/chrome/FooterExtras';
import { InstallAppSlot } from '@/features/storefront/pwa/InstallAppSlot';
import { useCategoryPath, useShelfCategories } from '@/features/storefront/hooks/useShelfCategories';
import { BlogLinks, MenuLink, PageLinks } from '@/features/storefront/layouts/chrome/ChromeParts';
import {
  FOOTER_CATEGORY_LIMIT,
  categoryColumns,
  menuItems,
  type FooterProps,
} from '@/features/storefront/layouts/chrome/types';
import { cn } from '@/lib/cn';

/**
 * The footer grid, by how many link columns stand between the wordmark and the
 * sign-up. Written out for the same reason Serra's and Voltic's are: Tailwind
 * reads these class names out of the source and never sees a string built at
 * runtime.
 */
const FOOTER_GRID: Record<number, string> = {
  0: 'lg:grid-cols-[1.4fr_1.2fr]',
  1: 'lg:grid-cols-[1.4fr_1fr_1.2fr]',
  2: 'lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]',
  3: 'lg:grid-cols-[1.4fr_1fr_1fr_1fr_1.2fr]',
  4: 'lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr_1.2fr]',
};

/**
 * One column: a sand heading in capitals over its links.
 *
 * The heading wears the accent because on the inverted ground it is the only
 * thing separating a column's name from its contents - a theme that turns its
 * footer over cannot do that with weight alone.
 */
function Column({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div>
      <p
        className="mb-5 text-[11px] uppercase tracking-[0.16em]"
        style={{ color: 'var(--storefront-accent)' }}
        aria-hidden={title ? undefined : true}
      >
        {title ?? ' '}
      </p>
      {children}
    </div>
  );
}

/**
 * The sign-up, which lives here rather than in a band of its own.
 *
 * A homeware shop asks once, in the fine print, and the fifteen per cent is
 * the reason anyone answers. Underlined rather than boxed: this footer has no
 * borders anywhere else on it and a bordered field would be the loudest thing
 * on the page's quietest band.
 *
 * Inert in the customiser preview for the same reason the newsletter *section*
 * is - a merchant designing their footer must not subscribe themselves to
 * their own shop.
 */
function Newsletter({ preview }: { preview?: boolean }) {
  const copy = useShopContent();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (preview || !email.trim()) return;

    setState('sending');
    try {
      await subscribeToNewsletter(email.trim());
      setState('done');
      setEmail('');
    } catch {
      setState('error');
    }
  }

  return (
    <Column title={copy('theme.newsletter_title')}>
      <p className="font-semibold">{copy('theme.newsletter_heading')}</p>
      <p className="mt-1 opacity-75">{copy('theme.newsletter_offer')}</p>

      {state === 'done' ? (
        <p className="mt-5" style={{ color: 'var(--storefront-accent)' }}>
          {copy('theme.newsletter_thanks')}
        </p>
      ) : (
        <form className="mt-5 flex items-center gap-3 border-b pb-2" onSubmit={submit}>
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={copy('theme.newsletter_placeholder')}
            aria-label={copy('login.email')}
            className="min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:opacity-50"
          />
          <button
            type="submit"
            aria-label={copy('theme.newsletter_subscribe')}
            disabled={state === 'sending'}
            className="shrink-0 disabled:opacity-50"
          >
            <ArrowRight className="size-4 rtl-flip" aria-hidden />
          </button>
        </form>
      )}

      {state === 'error' ? (
        <p className="mt-3 text-[12px] opacity-75">{copy('theme.newsletter_error')}</p>
      ) : null}
    </Column>
  );
}

/**
 * The back page: an inverted directory under the marquee band.
 *
 * The only dark thing on this shop apart from the announcement rail, which is
 * the shape the whole theme is built on - the page opens on a dark line, runs
 * white for its whole length, and closes on a dark block. Everything between
 * them is photography.
 *
 * The column arithmetic is Serra's and Voltic's, because the problem is the
 * same one: the merchant's footer menu, the shop's own shelves, its pages and
 * its blogs, each skipped when empty and the grid sized to what is left - the
 * alternative being a directory of headings with nothing under them.
 */
export const Footer: FC<FooterProps> = ({ storeName, footer, description, menu, preview }) => {
  const items = menuItems(menu);
  const shelfColumns = categoryColumns(
    useShelfCategories({ limit: FOOTER_CATEGORY_LIMIT, enabled: !preview }),
  );
  const categoryPath = useCategoryPath();
  const copy = useShopContent();
  const columns = (items.length > 0 ? 1 : 0) + shelfColumns.length + (preview ? 0 : 1);
  // The merchant may well have called their own menu "Shop" already, and two
  // columns under one heading read as a single list broken in half. Compared
  // against the shop's own word for the column, in the shopper's language.
  const shopTitle = copy('theme.footer_shop');
  const shelvesTitle =
    menu?.name.trim().toLowerCase() === shopTitle.trim().toLowerCase()
      ? copy('theme.departments')
      : shopTitle;

  return (
    <footer
      className="text-[13px]"
      style={{
        backgroundColor: 'var(--storefront-text)',
        color: 'var(--storefront-background)',
      }}
    >
      <StorefrontContainer
        className={cn('grid gap-12 pb-14 pt-16 text-start sm:grid-cols-2', FOOTER_GRID[columns])}
      >
        <div>
          <p
            className="text-3xl uppercase tracking-[0.2em]"
            style={{
              fontFamily: 'var(--storefront-heading-font)',
              fontWeight: 'var(--storefront-heading-weight)',
            }}
          >
            {storeName}
          </p>
          {description ? (
            <p className="mt-5 max-w-[24rem] leading-relaxed opacity-75">{description}</p>
          ) : null}
        </div>

        {items.length > 0 ? (
          <Column title={menu?.name ?? shopTitle}>
            <nav aria-label={menu?.name} className="flex flex-col items-start gap-3">
              {items.map((item) => (
                <MenuLink
                  key={`${item.label}:${item.url}`}
                  item={item}
                  preview={preview}
                  className="opacity-80 hover:opacity-100"
                />
              ))}
            </nav>
          </Column>
        ) : null}

        {shelfColumns.map((column, index) => (
          <Column key={column[0].id} title={index === 0 ? shelvesTitle : undefined}>
            <nav
              aria-label={
                index === 0 ? shelvesTitle : copy('theme.footer_continued', { title: shelvesTitle })
              }
              className="flex flex-col items-start gap-3"
            >
              {column.map((category) => (
                <Link
                  key={category.id}
                  to={categoryPath(category.slug)}
                  className="opacity-80 hover:opacity-100"
                >
                  {category.name}
                </Link>
              ))}
            </nav>
          </Column>
        ))}

        {preview ? null : (
          <Column title={copy('theme.footer_customer_care')}>
            <PageLinks className="mb-3 flex-col items-start justify-start gap-3 opacity-80" />
            <BlogLinks className="flex-col items-start justify-start gap-3 opacity-80" />
            <InstallAppSlot className="mt-4" />
            <AppStoreBadges footer={footer} preview={preview} className="mt-4" />
          </Column>
        )}

        <Newsletter preview={preview} />
      </StorefrontContainer>

      <div aria-hidden className="h-px opacity-20" style={{ backgroundColor: 'currentColor' }} />
      <StorefrontContainer className="flex flex-wrap justify-between gap-x-6 gap-y-2 py-6 text-[12px] opacity-60">
        <LanguageSwitcherSlot placement="footer" preview={preview} />
        <span>{copy('theme.prices_all_inr_gst')}</span>
        <span style={copyrightStyle(footer)}>
          &copy; {new Date().getFullYear()} {storeName} &middot;{' '}
          {copy('theme.powered_by', { brand: 'NeonCarts' })}
        </span>
      </StorefrontContainer>
    </footer>
  );
};
